import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { type FileOwner, filesQuery, openFile, type StoredFile } from '../api/documents.ts';
import { Button } from '../components/button.tsx';
import { icons } from '../components/icons.ts';

// CB-REQ DOC-01, FND-04 and DOC-03 at v1.959: a record's documents, the same panel on a quote, an
// invoice and a job. Preparing one renders it, keeps it, and opens it at once; every one kept stays
// listed, so what went to a client can be found again. Nothing is emailed from here (BKL-06).

const KIND_LABELS: Record<StoredFile['kind'], string> = {
  quote: 'Quote',
  tax_invoice: 'Invoice',
  pick_list: 'Pick list',
  docket: 'Delivery docket',
  logo: 'Logo',
  upload: 'Uploaded',
  crew_card: 'Card photo',
  insurance_certificate: 'Certificate of currency',
  safety_document: 'Printed with a sign-on sheet',
  venue_photo: 'Venue photo',
  venue_plan: 'Venue plan',
  venue_statement: 'Statement',
  adjustment_note: 'Adjustment note',
};

export interface DocumentAction {
  label: string;
  prepare: () => Promise<StoredFile>;
}

export function DocumentsPanel({
  owner,
  id,
  actions,
  onUpload,
}: {
  owner: FileOwner;
  id: string;
  actions: readonly DocumentAction[];
  /** DOC-03 at v1.959: a signed docket, or other paper, kept on the record. */
  onUpload?: ((file: File) => Promise<StoredFile>) | undefined;
}) {
  const files = useQuery(filesQuery(owner, id));
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: filesQuery(owner, id).queryKey });
  const prepare = useMutation({
    mutationFn: async (action: DocumentAction) => {
      const file = await action.prepare();
      await refresh();
      await openFile(file.id);
    },
  });
  const open = useMutation({ mutationFn: openFile });
  const picker = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      await onUpload!(file);
      await refresh();
    },
    onSettled: () => setUploading(false),
  });
  const error = prepare.error ?? open.error ?? upload.error;

  return (
    <section className="flex flex-col gap-3" aria-labelledby={`documents-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={`documents-${id}`} className="section-title flex-1">
          Documents
        </h2>
        <div className="flex flex-wrap gap-2">
          {actions.map((a) => (
            <Button
              key={a.label}
              variant="quiet"
              disabled={prepare.isPending}
              onClick={() => prepare.mutate(a)}
            >
              {prepare.isPending && prepare.variables === a ? 'Preparing…' : a.label}
            </Button>
          ))}
          {onUpload ? (
            <>
              <Button
                variant="quiet"
                disabled={upload.isPending}
                onClick={() => picker.current?.click()}
              >
                {uploading ? 'Uploading…' : 'Upload signed'}
              </Button>
              <input
                ref={picker}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="sr-only"
                aria-label="Upload a signed copy"
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0];
                  e.currentTarget.value = '';
                  if (!file) return;
                  setUploading(true);
                  upload.mutate(file);
                }}
              />
            </>
          ) : null}
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
      {files.data && files.data.length > 0 ? (
        <ul className="flex flex-col divide-y-2 divide-divider text-sm">
          {files.data.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <span>
                <span className="font-semibold">{f.filename}</span>
                <span className="text-muted">
                  {' '}
                  · {KIND_LABELS[f.kind]} · {when(f.createdAt)}
                  {f.termsVersion ? ` · terms v${f.termsVersion}` : ''}
                </span>
              </span>
              <button
                type="button"
                className="link cursor-pointer"
                aria-label={`Download ${f.filename}`}
                title="Download"
                disabled={open.isPending}
                onClick={() => open.mutate(f.id)}
              >
                <icons.download size={16} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : files.data ? (
        <p className="text-sm text-ink-soft">None prepared yet.</p>
      ) : null}
    </section>
  );
}

/** The instant a file was kept, in the reader's own zone. */
function when(instant: string): string {
  return Temporal.Instant.from(instant)
    .toZonedDateTimeISO(Temporal.Now.timeZoneId())
    .toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
}
